import * as React from 'react';

interface FeedSurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  rootRef: React.ForwardedRef<HTMLDivElement>;
  itemSlot: string;
}

interface Snapshot {
  scroll: HTMLElement;
  visible: boolean;
  anchors: { node: HTMLElement; top: number }[];
  focused: HTMLElement | null;
  focusedRow: HTMLElement | null;
  rowIndex: number;
  disclosures: { node: HTMLElement; hidden: boolean }[];
}

const FeedCoordinator = React.createContext<FeedSurface | null>(null);

/** Runs inside a new row boundary, before React places that boundary's DOM. */
function FeedInsertionSnapshot() {
  const coordinator = React.useContext(FeedCoordinator);
  React.useInsertionEffect(() => {
    coordinator?.captureCommit();
  }, [coordinator]);
  return null;
}

/** No DOM wrapper: descendant-owned rows participate in the feed commit. */
export class FeedRow extends React.Component<React.PropsWithChildren> {
  static contextType = FeedCoordinator;
  declare context: React.ContextType<typeof FeedCoordinator>;

  componentDidMount() {
    this.context?.scheduleCommit();
  }

  getSnapshotBeforeUpdate() {
    this.context?.captureCommit();
    return null;
  }

  componentDidUpdate() {
    this.context?.scheduleCommit();
  }

  componentWillUnmount() {
    this.context?.captureCommit();
    this.context?.scheduleCommit();
  }

  render() {
    return (
      <>
        <FeedInsertionSnapshot />
        {this.props.children}
      </>
    );
  }
}

/** Internal feed boundary: snapshot before React removes or resizes keyed rows. */
export class FeedSurface extends React.Component<FeedSurfaceProps> {
  private root: HTMLDivElement | null = null;
  private seen = new Set<string>();
  private arrivals = new Map<HTMLElement, Animation>();
  private media: MediaQueryList | null = null;
  private pending: Snapshot | null = null;
  private scheduled = false;
  private mounted = false;

  captureCommit() {
    if (this.mounted && !this.pending)
      this.pending = this.getSnapshotBeforeUpdate();
  }

  scheduleCommit() {
    if (!this.mounted || this.scheduled) return;
    this.scheduled = true;
    // Flush once after all descendant DOM mutations, before the next paint.
    queueMicrotask(() => {
      if (!this.scheduled || !this.mounted) return;
      this.commit(this.pending);
    });
  }

  private rows = () =>
    Array.from(
      this.root?.querySelectorAll<HTMLElement>(
        `[data-slot="${this.props.itemSlot}"]`,
      ) ?? [],
    );

  private forwardedRef: React.ForwardedRef<HTMLDivElement> | undefined;
  private setRoot: React.RefCallback<HTMLDivElement> | undefined;

  private rootCallback() {
    if (!this.setRoot || this.forwardedRef !== this.props.rootRef) {
      const ref = this.props.rootRef;
      this.forwardedRef = ref;
      this.setRoot = (node) => {
        this.root = node;
        if (typeof ref === 'function') return ref(node);
        if (ref) ref.current = node;
      };
    }
    return this.setRoot;
  }

  private reduce = () => {
    if (this.media?.matches) {
      for (const animation of this.arrivals.values()) animation.cancel();
      this.arrivals.clear();
    }
  };

  componentDidMount() {
    this.media =
      this.root?.ownerDocument.defaultView?.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ) ?? null;
    this.media?.addEventListener('change', this.reduce);
    for (const row of this.rows()) {
      const id = row.dataset.feedId ?? row.id;
      if (id) this.seen.add(id);
    }
    this.mounted = true;
  }

  getSnapshotBeforeUpdate(): Snapshot | null {
    const root = this.root;
    if (!root) return null;
    const view = root.ownerDocument.defaultView;
    if (!view) return null;
    let scroll: HTMLElement = root;
    while (
      scroll.parentElement &&
      !/(auto|scroll)/.test(view.getComputedStyle(scroll).overflowY)
    ) {
      scroll = scroll.parentElement;
    }
    if (!/(auto|scroll)/.test(view.getComputedStyle(scroll).overflowY)) {
      scroll = root.ownerDocument.scrollingElement as HTMLElement;
    }
    const rows = this.rows();
    let top = 0;
    let bottom = view.innerHeight;
    let left = 0;
    let right = view.innerWidth;
    for (
      let ancestor: HTMLElement | null = root;
      ancestor;
      ancestor = ancestor.parentElement
    ) {
      const style = view.getComputedStyle(ancestor);
      const rect = ancestor.getBoundingClientRect();
      if (/(auto|scroll|hidden|clip)/.test(style.overflowY)) {
        top = Math.max(top, rect.top + ancestor.clientTop);
        bottom = Math.min(
          bottom,
          rect.top + ancestor.clientTop + ancestor.clientHeight,
        );
      }
      if (/(auto|scroll|hidden|clip)/.test(style.overflowX)) {
        left = Math.max(left, rect.left + ancestor.clientLeft);
        right = Math.min(
          right,
          rect.left + ancestor.clientLeft + ancestor.clientWidth,
        );
      }
    }
    const rect = root.getBoundingClientRect();
    const visible =
      root.isConnected &&
      scroll.isConnected &&
      root.getClientRects().length > 0 &&
      bottom > top &&
      right > left &&
      rect.bottom > top &&
      rect.top < bottom &&
      rect.right > left &&
      rect.left < right;
    const focused = root.ownerDocument.activeElement;
    const focusedRow = rows.find((row) => row.contains(focused)) ?? null;
    return {
      scroll,
      visible,
      anchors:
        visible && scroll.scrollTop > 0
          ? rows
              .filter((row) => {
                const rect = row.getBoundingClientRect();
                return (
                  bottom > top &&
                  right > left &&
                  rect.bottom > top &&
                  rect.top < bottom &&
                  rect.right > left &&
                  rect.left < right
                );
              })
              .map((node) => ({ node, top: node.getBoundingClientRect().top }))
          : [],
      focused:
        focused instanceof view.HTMLElement && root.contains(focused)
          ? focused
          : null,
      focusedRow,
      rowIndex: focusedRow ? rows.indexOf(focusedRow) : -1,
      disclosures: Array.from(
        root.querySelectorAll<HTMLElement>('[data-feed-disclosure]'),
      ).map((node) => ({ node, hidden: Boolean(node.hidden) })),
    };
  }

  componentDidUpdate(
    _props: FeedSurfaceProps,
    _state: unknown,
    snapshot: Snapshot | null,
  ) {
    this.commit(snapshot);
  }

  private commit(snapshot: Snapshot | null) {
    this.scheduled = false;
    this.pending = null;
    const rows = this.rows();
    if (snapshot) {
      // Rows may leave the clip after a large mutation; only the feed must
      // remain visible. Anchor eligibility comes from the fresh pre-mutation snapshot.
      const current = this.getSnapshotBeforeUpdate();
      const anchor =
        current?.visible && current.scroll === snapshot.scroll
          ? snapshot.anchors.find(({ node }) => node.isConnected)
          : undefined;
      if (anchor)
        snapshot.scroll.scrollTop +=
          anchor.node.getBoundingClientRect().top - anchor.top;
      const active = this.root?.ownerDocument.activeElement;
      if (
        snapshot.focused &&
        (!active ||
          active === snapshot.focused ||
          active === this.root?.ownerDocument.body) &&
        (!snapshot.focused.isConnected ||
          snapshot.focused.getClientRects().length === 0)
      ) {
        const row = snapshot.focusedRow?.isConnected
          ? snapshot.focusedRow
          : rows[Math.min(snapshot.rowIndex, rows.length - 1)];
        const target =
          row?.querySelector<HTMLElement>(
            'button:not(:disabled), a[href], [tabindex="0"]',
          ) ??
          row ??
          this.root;
        if (target) {
          if (
            !target.hasAttribute('tabindex') &&
            !target.matches('button, a[href]')
          )
            target.tabIndex = -1;
          target.focus({ preventScroll: true });
        }
      }
    }
    for (const [row, animation] of this.arrivals) {
      if (!row.isConnected || row.hidden) {
        animation.cancel();
        this.arrivals.delete(row);
      }
    }
    const view = this.root?.ownerDocument.defaultView;
    if (!view || !this.root) return;
    const style = view.getComputedStyle(this.root);
    const duration = Number.parseFloat(
      style.getPropertyValue('--motion-duration-fast'),
    );
    const easing = style.getPropertyValue('--motion-easing-standard').trim();
    const canAnimate =
      Number.isFinite(duration) && duration > 0 && easing !== '';
    for (const row of rows) {
      const id = row.dataset.feedId ?? row.id;
      if (!id || this.seen.has(id)) continue;
      this.seen.add(id);
      if (this.media?.matches || !canAnimate) continue;
      this.fade(row, duration, easing);
    }
    for (const disclosure of snapshot?.disclosures ?? []) {
      if (
        disclosure.hidden &&
        disclosure.node.isConnected &&
        !disclosure.node.hidden &&
        !this.media?.matches &&
        canAnimate
      ) {
        this.fade(disclosure.node, duration, easing);
      }
    }
  }

  private fade(node: HTMLElement, duration: number, easing: string) {
    this.arrivals.get(node)?.cancel();
    const animation = node.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration,
      easing,
    });
    this.arrivals.set(node, animation);
    animation.onfinish = () => {
      if (this.arrivals.get(node) === animation) this.arrivals.delete(node);
    };
  }

  componentWillUnmount() {
    this.mounted = false;
    this.media?.removeEventListener('change', this.reduce);
    for (const animation of this.arrivals.values()) animation.cancel();
    this.arrivals.clear();
  }

  render() {
    const { rootRef: _rootRef, itemSlot: _itemSlot, ...props } = this.props;
    return (
      <FeedCoordinator.Provider value={this}>
        <div {...props} ref={this.rootCallback()} />
      </FeedCoordinator.Provider>
    );
  }
}
